import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/storeHooks';
import { setUIButtons } from '../navigation/UIButtonToolbarSlice';
import Dropdown from '../common/Dropdown/Dropdown';
import { fetchInitialStateAsync } from '../group-status/TableViewSlice';
import { fetchInitialGroupStatusStateAsync, setSelectedView } from '../group-status/GroupStatusSlice';
import '../group-status/GroupStatus.scss';
import './CustomViews.scss';
import Tooltip from '../common/tooltip/ToolTip';
import { fetchWellControlActions, fetchEnabledStatus } from '../asset/AssetDataSlice';
import { exportToExcel } from '../../utilities/ExportToExcel';
import { useParams } from 'react-router-dom';
import { ReactComponent as NoAssetsImage } from './../../images/no-assets.svg';
import UIButtonToolbar from './../navigation/components/UIButtonToolbar';
import { updateSelectedAsset } from '../navigation/AssetGroupsSlice';
import Loader from '../common/Loader/Loader';
import useFullScreenToggle from '../../hooks/useFullScreenToggle';
import TableHeader from '../../components/data-tables/TableHeader';
import TableToolbar from '../../components/data-tables/TableToolbar';
import SelectGroup from '../../onboarding/select-group/SelectGroup';
import HeaderElements from '../navigation/components/HeaderElements';
import CustomViewDataTable from './CustomViewDataTable';
import MultiWellControlActions from './MultiwellControlActions/MultiWellControlActions';
import useWellControlActions from './useWellControlActions';
import SelectedWellFilters from '../well-filters/SelectedWellFilters';
import { removeFilter } from '../well-filters/WellFilterSlice';
import { hasActiveRelevantFilters, createFilterFunction } from '../../utilities/GlobalFilterUtil';
import CustomViewCardViewer from './CardViewerTab/CustomViewCardViewer';
import MultiWellControlConfirm from './MultiWellControlConfirm/MultiWellControlConfirm';
import WellEditModal from '../../components/well-edit-view/WellEditViewModals';
import { sanitizeForLog } from '../../utilities/logSanitizer';
import NavigationDrawerItem from '../navigation/components/NavigationDrawerItem';
import { formatDate } from '../../utilities/dateHelpers';
import {
  selectProgressDisplay,
  setActionCommand,
  setSelectedGroupName,
} from './MultiWellControlConfirm/WellControlSlice';
import { ALLY_CONFIGURATIONS } from '../../services/configuration/config';
import MenuNavIcon from '../navigation/components/NavMenuIcon';

interface UrlParams {
  groupName: string;
}

const CustomViews = () => {
  const tableViews = useAppSelector((state) => state.tableView.tableViews);
  const selectedView = useAppSelector((state) => state.groupStatus.selectedView);
  const groupStatus = useAppSelector((state) => state.groupStatus.groupStatus);
  const selectedGroup = useAppSelector((State) => State.assetGroups.selectedGroup);
  const assets = useAppSelector((state) => state.assetGroups);
  const hasWellFilters = useAppSelector((state: any) => state.WellFiltersSlice.hasSelectedFilters);
  const selectedFilters = useAppSelector((state) => state.WellFiltersSlice.selectedFilters);
  const filteredAssetList = useAppSelector((state) => state.WellFiltersSlice.filteredAssetList);
  const filteredAssetListLoading = useAppSelector((state) => state.WellFiltersSlice.filteredAssetListLoading);
  const isTableScanning = useAppSelector((state) => state.wellControl.isTableScanning);
  const isTableOpen = useAppSelector((state) => state.wellControl.isTableOpen);

  const progressDisplay = useAppSelector(selectProgressDisplay);

  const { groupName } = useParams<keyof UrlParams>() as UrlParams;
  const wellGroupName = groupName ?? selectedGroup?.assetGroupName;
  const groupStatusLoading = useAppSelector((state) => state.groupStatus.groupStatusLoading);
  const [isWellEditModalOpen, setWellEditModalOpen] = useState(false);
  const [isFetchingGroupData, setIsFetchingGroupData] = useState(false);

  const groupStatusPromiseRef = useRef<any>(null);
  const currentRequestIdRef = useRef<number>(0);

  const groupHasRLWells =
    Array.isArray(selectedGroup?.assets) &&
    selectedGroup?.assets.some((well: any) => well?.industryApplicationId === 3);

  const [activeTabId, setActiveTabId] = useState(0);
  const [prevGroupName, setPrevGroupName] = useState('');
  const [prevSelectedView, setPrevSelectedView] = useState<any>();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRows, setFilteredRows] = useState<any[]>([]);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);
  const [pendingColumnKeys, setPendingColumnKeys] = useState<string[]>([]);
  const [showWellControlActions, setShowWellControlActions] = useState(false);
  const [applyTrigger, setApplyTrigger] = useState(false);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string>('');

  const WELL_SELECTION_LIMIT = ALLY_CONFIGURATIONS.MAX_WELLS_SELECTABLE;
  const selectedCardsCount = selectedAssetIds.length;

  const wellsSelectedBadge =
    selectedCardsCount >= WELL_SELECTION_LIMIT ? `${selectedCardsCount} wells selected` : undefined;

  useEffect(() => {
    if (isTableOpen && selectedAssetIds.length > 0 && !showConfirmModal) {
      setShowConfirmModal(true);
    }
  }, [isTableOpen, selectedAssetIds.length, showConfirmModal, selectedAction]);

  const rPermission = { unProtected: true };

  const { executeAction } = useWellControlActions(selectedAssetIds, (success: boolean, message?: string) => {
    // Sanitize message to prevent log injection (CWE-117)
    console.log('Action completed:', { status: success ? 'Success' : 'Failed', message: sanitizeForLog(message) });
    if (success) {
      // setShowConfirmModal(false);
    }
  });

  // Create selected wells data for confirmation modal
  const selectedWellsData = useMemo(() => {
    if (!selectedAssetIds.length || !filteredRows.length) return [];

    return selectedAssetIds
      .map((assetId) => {
        const row = filteredRows.find((r: any) => r.Assetguid === assetId);
        if (!row) return null;

        // Try different variations of column names for last good scan
        const lastGoodScan =
          row['Last good scan'] ||
          row['Last Good Scan'] ||
          row['LastGoodScan'] ||
          row['Last Scan'] ||
          row['Scan'] ||
          '-';

        // Try different variations of column names for communication status
        const commsStatus =
          row['Comms status'] ||
          row['Comms Status'] ||
          row['Communication Status'] ||
          row['Comm Status'] ||
          row['CommStatus'] ||
          row['Status'] ||
          '-';

        return {
          assetId: assetId,
          wellName: row.Well || row.well || row['Well name'] || 'Unknown well',
          lastGoodScan: lastGoodScan,
          commsStatus: commsStatus,
        };
      })
      .filter(Boolean) as Array<{
        assetId: string;
        wellName: string;
        lastGoodScan: string;
        commsStatus: string;
      }>;
  }, [selectedAssetIds, filteredRows]);

  const customViewTabs = [
    {
      name: 'Group status',
      active: activeTabId === 0,
      id: 0,
    },
    ...(groupHasRLWells
      ? [
        {
          name: 'Card viewer',
          active: activeTabId === 1,
          id: 1,
        },
      ]
      : []),
  ];

  useEffect(() => {
    if (!groupHasRLWells && activeTabId === 1) {
      setActiveTabId(0);
      return;
    }
  }, [groupHasRLWells]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setUIButtons(customViewTabs));
    if (activeTabId === 0) {
      dispatch(fetchInitialStateAsync());
    }
  }, [activeTabId]);

  useEffect(() => {
    if (tableViews && tableViews.values.length > 0 && groupName) {
      let viewId = selectedView?.id;
      const currentView = selectedView;
      if (!viewId) {
        const previewView = tableViews.values.filter((view) => view.isSelectedView)[0] ?? tableViews?.values[0];
        viewId = previewView.id;

        dispatch(setSelectedView({ selectedView: previewView }));
      }

      if (viewId) {
        const groupStatusData = {
          viewId: viewId,
          group: groupName,
        };

        // Only make request if group or view actually changed
        const groupChanged = groupName !== prevGroupName;
        const viewChanged = currentView && prevSelectedView && prevSelectedView?.name !== currentView?.name;

        if (groupChanged || viewChanged) {
          // Update state tracking
          setPrevGroupName(groupName);
          setPrevSelectedView(currentView);
          setIsFetchingGroupData(true);

          // Increment request counter to track this as the most recent request
          currentRequestIdRef.current += 1;
          const thisRequestId = currentRequestIdRef.current;

          // Reset selections when group changes
          if (groupChanged) {
            setSelectedAssetIds([]);
          }

          // Cancel previous group status request if it exists
          if (groupStatusPromiseRef.current) {
            groupStatusPromiseRef.current.abort();
          }

          // Create new request and store the promise for cancellation
          groupStatusPromiseRef.current = dispatch(fetchInitialGroupStatusStateAsync(groupStatusData));

          groupStatusPromiseRef.current
            .then(() => {
              // Only update loading state if this is still the most recent request
              if (thisRequestId === currentRequestIdRef.current) {
                setIsFetchingGroupData(false);
              }
            })
            .catch((error: any) => {
              // Only update loading state if this is still the most recent request
              if (thisRequestId === currentRequestIdRef.current) {
                setIsFetchingGroupData(false);
              }
              // Only log errors that weren't due to abortion
              if (error.name !== 'AbortError') {
                console.error('Group status request failed:', error);
              }
            });
        }
      }
    }
  }, [tableViews, selectedGroup, groupName, selectedView, dispatch]);

  // Cleanup effect that only runs on unmount
  useEffect(() => {
    return () => {
      if (groupStatusPromiseRef.current) {
        groupStatusPromiseRef.current.abort();
      }
    };
  }, []);

  function onSelectedViewChange(localSelectedView: any) {
    const selectedViewDetails = tableViews?.values.filter((view) => view.name == localSelectedView.name);
    setPrevSelectedView(selectedView);
    dispatch(
      setSelectedView({
        selectedView: selectedViewDetails && selectedViewDetails?.length > 0 ? selectedViewDetails[0] : null,
      }),
    );

    if (selectedViewDetails && selectedViewDetails.length > 0) {
      const view = selectedViewDetails[0];
      if (view.name === 'Card Viewer') {
        setActiveTabId(1);
      } else {
        setActiveTabId(0);
      }
    }
  }
  const handleAssetDetailsRowClick = React.useCallback(
    (asset: any) => {
      if (!asset || !asset.values) return; // Prevent error if asset or asset.values is undefined
      const assetName = asset.values.Well;
      const assetId = asset.values.Assetguid;
      dispatch(fetchWellControlActions(assetId));
      dispatch(fetchEnabledStatus(assetId));

      dispatch(
        updateSelectedAsset({
          groupId: selectedGroup?.id,
          assetId: assetName,
          searchString: undefined,
          assetIdGuid: assetId,
        }),
      );
    },
    [selectedGroup],
  );

  useEffect(() => {
    const rows = groupStatus?.values?.rows || [];

    // Apply well filter if active filters exist
    const hasActiveFilters = hasWellFilters && hasActiveRelevantFilters(selectedFilters);

    const filterFunction = createFilterFunction(hasActiveFilters, filteredAssetList, 'Assetguid');
    const filteredData = filterFunction(rows);

    setFilteredRows(filteredData);
  }, [groupStatus, hasWellFilters, selectedFilters, filteredAssetList]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  useEffect(() => {
    const columns = groupStatus?.values?.columns || [];
    const keys = columns.map((col: any) => col.accessor);
    setVisibleColumnKeys(keys);
    setPendingColumnKeys(keys);
  }, [groupStatus]);

  const allColumnAccessors = groupStatus?.values?.columns?.map((col: any) => col.accessor) || [];
  const effectivePendingColumnKeys =
    pendingColumnKeys.length === 0 && allColumnAccessors.length > 0 ? allColumnAccessors : pendingColumnKeys;

  const handleColumnToggle = (columnId: string) => {
    setPendingColumnKeys((prev) => {
      if (prev.includes(columnId)) {
        return prev.filter((id) => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };

  const handleUpdateColumns = (updated: { id: string; Header: string; isVisible: boolean }[]) => {
    setPendingColumnKeys(updated.filter((col) => col.isVisible).map((col) => col.id));
  };

  const handleApplyClicked = () => {
    setApplyTrigger(true);
    setTimeout(() => {
      setApplyTrigger(false);
    }, 1000);

    const visibleOrder = new Map(pendingColumnKeys.map((key, index) => [key, index]));

    const visibleColumnsSorted = (groupStatus?.values?.columns || [])
      .filter((col: any) => visibleOrder.has(col.accessor))
      .sort((a: any, b: any) => visibleOrder.get(a.accessor)! - visibleOrder.get(b.accessor)!);

    setVisibleColumnKeys(visibleColumnsSorted.map((col: any) => col.accessor));
  };

  const onExportXLS = () => {
    const columns = groupStatus?.values?.columns || [];
    const rows = groupStatus?.values?.rows || [];

    // Create column mapping
    const columnHeaders: Record<string, string> = {};
    const exportColumns: string[] = [];

    columns.forEach((col: any) => {
      columnHeaders[col.accessor] = col.Header || col.accessor;
      exportColumns.push(col.accessor);
    });

    const fileName = `${wellGroupName || 'group_status'}-${new Date()
      .toISOString()
      .slice(0, 10)
      .replace(/-/g, '')}.xlsx`;

    const formattedRows = rows.map((row: any) => {
      const updatedRow = { ...row };

      Object.keys(updatedRow).forEach((key) => {
        const header = columnHeaders[key]?.toLowerCase();
        if (
          (header === 'last good scan' || header === 'scan' || header === 'last scan') &&
          updatedRow[key]
        ) {
          updatedRow[key] = formatDate(updatedRow[key], true);
        }
      });

      return updatedRow;
    });


    exportToExcel(formattedRows, exportColumns, columnHeaders, fileName);

  };

  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  useFullScreenToggle(setIsFullScreen);

  const isAssetExist = () => {
    return selectedGroup?.assets && selectedGroup?.assets.length > 0;
  };

  function onStausTabChanged(id: number) {
    setActiveTabId(id);
  }

  const wellControlActionsRef = useRef<HTMLDivElement>(null);

  const openWellEditViewModal = () => {
    setWellEditModalOpen(true);
  };

  useEffect(() => {
    if (!showWellControlActions) return;

    function handleClickOutside(event: MouseEvent) {
      if (wellControlActionsRef.current && !wellControlActionsRef.current.contains(event.target as Node)) {
        setShowWellControlActions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showWellControlActions]);

  useEffect(() => {
    dispatch(setActionCommand(selectedAction));
  }, [selectedAction, dispatch]);

  return (
    <>
      <div className='group-status-main-header custom-view-main-header'>
        <div className='nav-menu-icon-header-container'>
          <MenuNavIcon />
          <span className='group-status-group-name'>Custom views</span>
        </div>
        <div className='custom-views-header-elements'>
          <HeaderElements showFilterIcon={true} />
        </div>
      </div>
      <div>
        <SelectedWellFilters
          filters={selectedFilters}
          onRemoveFilter={(filterToRemove) => dispatch(removeFilter(filterToRemove))}
        />
      </div>
      <div className='custom-view-layout flex overflow-hidden' style={{ backgroundColor: 'transparent' }}>
        <div
          className={`custom-views-navigation-drawer-container flex-shrink-0 ${hasWellFilters ? 'has-filters' : ''}`}
        >
          <NavigationDrawerItem />
        </div>

        {selectedGroup ? (
          <div
            className={`flex-1 min-w-0 overflow-auto custom-view-container ${hasWellFilters ? 'has-filters-custom-views-scrollable-content' : 'custom-views-scrollable-content'}`}
          >
            {isAssetExist() || assets?.loading ? (
              <>
                {assets?.loading && !groupStatusLoading && (
                  <span className='absolute top-1/2 left-[calc(60%+50px)] transform -translate-x-1/2 -translate-y-1/2'>
                    <Loader />
                  </span>
                )}
                <div className={`group-status-wrapper-ui ${isFullScreen ? 'group-status-full-screen' : ''}`}>
                  <div className='group-status-container custom-view-right-container'>
                    <TableHeader
                      title={wellGroupName}
                      buttonLabel='Edit view'
                      onAddTestClick={openWellEditViewModal}
                      addIcon={false}
                      rPermission={rPermission}
                      badge={wellsSelectedBadge}
                    />
                    <div className='custom-view-tabs-container'>
                      <UIButtonToolbar uiButtons={customViewTabs} buttonClicked={onStausTabChanged} />
                    </div>
                    {activeTabId === 0 && (
                      <>
                        <TableToolbar
                          rPermission={rPermission}
                          ref={null}
                          columns={
                            groupStatus?.values?.columns
                              ? groupStatus.values.columns.map((col: any) => ({
                                id: col.accessor,
                                Header: col.Header,
                                isVisible: effectivePendingColumnKeys.includes(col.accessor),
                              }))
                              : []
                          }
                          onColumnToggle={handleColumnToggle}
                          onSaveColumns={() => {
                            /* TODO: implement save columns */
                          }}
                          onSearch={handleSearch}
                          onUpdateColumns={handleUpdateColumns}
                          onApplyClicked={handleApplyClicked}
                          saveConfigHasbeenClicked={() => {
                            /* TODO: implement save config */
                          }}
                          resetToDefaultSystemClicked={() => {
                            /* TODO: implement reset */
                          }}
                          onExportXLS={onExportXLS}
                          wellTestTable={false}
                          hideAllInstallations={true}
                          isActionsButtonVisible={true}
                          onActionsClick={() => {
                            dispatch(setSelectedGroupName(groupName));
                            setShowWellControlActions(true);
                          }}
                          disableActions={isTableScanning && progressDisplay !== 100}
                          isActionsDropdownOpen={showWellControlActions}
                          rightToolbarContent={
                            <Tooltip content='Available views' direction='top'>
                              <div className='table-view-ddn'>
                                <Dropdown
                                  assets={
                                    Array.isArray(tableViews?.values)
                                      ? (tableViews?.values?.map((tableView) => {
                                        return {
                                          name: tableView.name,
                                          isSelected: selectedView && selectedView?.name === tableView.name,
                                        };
                                      }) as any)
                                      : []
                                  }
                                  defaultOption={selectedView?.name}
                                  showEyeIcon={false}
                                  onChange={onSelectedViewChange}
                                  height={'40px'}
                                  width={'30vh'}
                                />
                              </div>
                            </Tooltip>
                          }
                          isColumnsVisible={false}
                        />
                        <span className='custom-view-well-control-container'>
                          {showWellControlActions && (
                            <div className='custom-view-well-control-actions-container' ref={wellControlActionsRef}>
                              <MultiWellControlActions
                                selectedAssetIds={selectedAssetIds}
                                onScanComplete={(success, message) => {
                                  // Sanitize message to prevent log injection (CWE-117)
                                  console.log('Scan completed', {
                                    status: success ? 'Success' : 'Failed',
                                    message: sanitizeForLog(message),
                                  });
                                }}
                                onActionSelected={(action) => {
                                  setSelectedAction(action);
                                  setShowConfirmModal(true);
                                }}
                              />
                            </div>
                          )}

                          <MultiWellControlConfirm
                            isOpen={showConfirmModal}
                            handleCancel={() => setShowConfirmModal(false)}
                            selectedWells={selectedWellsData}
                            actionCommand={selectedAction}
                            executeActionFn={() => executeAction(selectedAction)}
                            onRemoveAsset={(assetId) => {
                              setSelectedAssetIds((prev) => prev.filter((id) => id !== assetId));
                            }}
                          />
                        </span>
                        {(groupStatusLoading || filteredAssetListLoading || isFetchingGroupData) && !assets?.loading ? (
                          <div className='custom-view-group-status-loader'>
                            <Loader />
                          </div>
                        ) : (
                          groupStatus?.values?.columns &&
                          groupStatus?.values?.columns?.length > 0 && (
                            <CustomViewDataTable
                              columns={(groupStatus?.values?.columns || [])
                                .filter((col: any) => visibleColumnKeys.includes(col.accessor))
                                .sort(
                                  (a: any, b: any) =>
                                    visibleColumnKeys.indexOf(a.accessor) - visibleColumnKeys.indexOf(b.accessor),
                                )}
                              data={filteredRows}
                              rawData={groupStatus?.values?.rowValues}
                              handleAssetDetailsRowClick={handleAssetDetailsRowClick}
                              groupName={groupName}
                              searchTerm={searchTerm}
                              applyTrigger={applyTrigger}
                              selectedAssetIds={selectedAssetIds}
                              onSelectedAssetIdsChange={setSelectedAssetIds}
                            />
                          )
                        )}
                      </>
                    )}
                    {activeTabId === 1 && groupHasRLWells && (
                      <div className='custom-view-card-viewer-container' id='card-viewer-scroll-container'>
                        <CustomViewCardViewer groupName={wellGroupName} />
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className={`group-status-wrapper-ui ${isFullScreen ? 'group-status-full-screen' : ''}`}>
                  <div className='group-status-container custom-view-right-container'>
                    <TableHeader
                      title={wellGroupName}
                      buttonLabel='Edit view'
                      onAddTestClick={openWellEditViewModal}
                      addIcon={false}
                      rPermission={rPermission}
                      badge={wellsSelectedBadge}
                    />
                    <div className='select-group-container'>
                      <NoAssetsImage />
                      <h6>No assets found</h6>
                      <p className='mt-3'>Your assets will show here once they are added to this group.</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div
            className={`flex-1 custom-view-container ${hasWellFilters ? 'has-filters-custom-views-scrollable-content' : 'custom-views-scrollable-content'}`}
          >
            <div className='group-status-container custom-view-right-container'>
              <SelectGroup />
            </div>
          </div>
        )}
      </div>

      <WellEditModal isOpen={isWellEditModalOpen} isAdd={false} onClose={() => setWellEditModalOpen(false)} />
    </>
  );
};

export default CustomViews;

